import { POLICY_KEYS } from '../constants/legalPolicies';

/** Full policy set required for payouts — legacy NDA-only does not count. */
function hasAllLegalAcceptances(user) {
  const legal = user?.legalAcceptances;
  if (!legal || typeof legal !== 'object') return false;
  return POLICY_KEYS.every((key) => Boolean(legal[key]?.accepted));
}

function hasIdentityMedia(user) {
  // Retain earlier intro videos; live photo only required when neither exists
  return Boolean(user?.liveCaptureImageUrl || user?.introVideoUrl);
}

/** An admin waiver stands in for the tester's own ownership confirmation. */
function hasPayoutComplianceWaiver(user) {
  return Boolean(user?.payoutComplianceWaivedAt);
}

function hasPayPal(user) {
  if (!user?.paypalAccountId) return false;
  // A waiver clears the attestation requirement but still needs a destination
  // account to pay: country and currency remain mandatory.
  const ownershipOk =
    Boolean(user?.paypalOwnershipConfirmedAt) || hasPayoutComplianceWaiver(user);
  return Boolean(
    ownershipOk && user?.paypalAccountCountry && user?.paypalPreferredCurrency
  );
}

function hasTax(user) {
  return Boolean(
    user?.taxResidenceCountry &&
      user?.servicesOutsideIndiaConfirmed &&
      user?.noIndiaPeDeclared &&
      user?.localTaxResponsibilityAccepted &&
      user?.taxDeclarationAcceptedAt
  );
}

/**
 * Gaps for testers who joined before compliance features existed.
 * Forces re-acceptance of full legal policies, identity media, and payout details.
 */
export function getTesterPaymentEligibilityGaps(user) {
  if (!user || user.role !== 'tester') {
    return { needsModal: false, steps: [], waitingOnReview: false };
  }

  const steps = [];

  if (!hasAllLegalAcceptances(user)) {
    steps.push({
      id: 'agreements',
      title: 'Accept legal agreements',
      description:
        'New payout rules require accepting all policies (Tester Agreement, Privacy, Payment, Tax, and NDA). A previous NDA-only acceptance is not enough.',
    });
  }

  if (!hasIdentityMedia(user)) {
    steps.push({
      id: 'identity_media',
      title: 'Live photo capture',
      description: 'Capture a live selfie with timestamp and location for identity verification.',
    });
  }

  if (!hasPayPal(user) || !hasTax(user)) {
    steps.push({
      id: 'payout_details',
      title: 'PayPal & tax declarations',
      description: 'Add your PayPal account and complete tax / contractor declarations.',
    });
  }

  const kycStatus = String(user.kycStatus || '').toLowerCase();
  const waitingOnReview =
    steps.length === 0 &&
    (kycStatus === 'pending' ||
      kycStatus === 'rejected' ||
      String(user.sanctionsStatus || 'pending') !== 'clear');

  if (kycStatus === 'rejected') {
    steps.push({
      id: 'kyc_resubmit',
      title: 'Resubmit identity documents',
      description: 'Your identity verification was rejected. Upload a new ID document to continue.',
    });
  }

  return {
    needsModal: steps.length > 0,
    steps,
    waitingOnReview,
    hasIntroVideo: Boolean(user.introVideoUrl),
    hasLiveCapture: Boolean(user.liveCaptureImageUrl),
    isLegacyNdaOnly:
      Boolean(user?.ndaAcceptance?.accepted) && !hasAllLegalAcceptances(user),
  };
}

export { hasIdentityMedia, hasAllLegalAcceptances };
